import { useGlobalConfig } from './useGlobalConfig';

export const useFormatting = () => {
  const { config, formatCurrency, formatDate, formatDateTime } = useGlobalConfig();

  const formatAmount = (amount: number, includeCurrency = true): string => {
    if (includeCurrency) {
      return formatCurrency(amount);
    }
    return amount.toFixed(2);
  };

  const formatPercentage = (value: number, decimals = 1): string => {
    return `${value.toFixed(decimals)}%`;
  };

  const formatNumber = (value: number, decimals = 0): string => {
    return value.toLocaleString(undefined, {
      minimumFractionDigits: decimals,
      maximumFractionDigits: decimals,
    });
  };

  const formatDateShort = (date: Date | string): string => {
    const dateObj = typeof date === 'string' ? new Date(date) : date;
    return formatDate(dateObj);
  };

  const formatDateTimeFull = (date: Date | string): string => {
    const dateObj = typeof date === 'string' ? new Date(date) : date;
    return formatDateTime(dateObj);
  };

  const formatRelativeTime = (date: Date | string): string => {
    const dateObj = typeof date === 'string' ? new Date(date) : date;
    const now = new Date();
    const diffInSeconds = Math.floor((now.getTime() - dateObj.getTime()) / 1000);

    if (diffInSeconds < 60) {
      return 'Just now';
    } else if (diffInSeconds < 3600) {
      const minutes = Math.floor(diffInSeconds / 60);
      return `${minutes} minute${minutes > 1 ? 's' : ''} ago`;
    } else if (diffInSeconds < 86400) {
      const hours = Math.floor(diffInSeconds / 3600);
      return `${hours} hour${hours > 1 ? 's' : ''} ago`;
    } else if (diffInSeconds < 604800) {
      const days = Math.floor(diffInSeconds / 86400);
      return `${days} day${days > 1 ? 's' : ''} ago`;
    } else {
      return formatDateShort(dateObj);
    }
  };

  const getCurrencySymbol = (): string => {
    return config?.currency.symbol || 'R';
  };

  const getCurrencyCode = (): string => {
    return config?.currency.default || 'ZAR';
  };

  const getFacilityName = (): string => {
    return config?.facility.name || 'Breeding Facility';
  };

  const getTimezone = (): string => {
    return config?.system.timezone || 'Africa/Johannesburg';
  };

  const getDateFormat = (): string => {
    return config?.system.dateFormat || 'DD/MM/YYYY';
  };

  const getLanguage = (): string => {
    return config?.system.language || 'en';
  };

  return {
    // Currency formatting
    formatCurrency,
    formatAmount,
    getCurrencySymbol,
    getCurrencyCode,

    // Date/time formatting
    formatDate,
    formatDateTime,
    formatDateShort,
    formatDateTimeFull,
    formatRelativeTime,

    // Number formatting
    formatNumber,
    formatPercentage,

    // System info
    getFacilityName,
    getTimezone,
    getDateFormat,
    getLanguage,

    // Raw config access
    config,
  };
}; 